import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import {
  getToken,
  clearAllTokens,
  setToken,
  setRefreshToken,
} from "../lib/auth";
import { apiAlova, authAlova } from "../lib/alova";
import type { ApiResponse } from "../types/api";
import { isResponseSuccess } from "../types/api";
import { encryptData, decryptData } from "../lib/helpers";

const STORAGE_KEY = "user";

/* ==================== Types ==================== */

export interface User {
  // Adjust fields to match your real profile response
  id?: number;
  username?: string;
  email?: string;
  unit_kerja?: string;
  [key: string]: any;
}

interface AuthState {
  user: User | null;
  loading: boolean;
}

interface AuthActions {
  setUser: (u: User | null) => void;
  clearUser: () => void;
  logout: () => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

type AuthContextType = [AuthState, AuthActions] | null;

/* ==================== Context ==================== */

const AuthContext = createContext<AuthContextType>(null);

/* ==================== API Profile ==================== */

export async function getProfile(force: boolean = false): Promise<User> {
  const res = await apiAlova
    .Get<ApiResponse<User>>(`/auth/profile?${force ? `ts=${Date.now()}` : ""}`)
    .send();

  if ("error" in res || !res.data) {
    throw new Error(res.message || "Gagal ambil profile");
  }

  return res.data;
}

interface LoginData {
  tokens: {
    access_token: string;
    refresh_token: string;
  };
}

export async function login(
  username: string,
  password: string
): Promise<{ success: boolean; message: string }> {
  const res = await authAlova
    .Post<ApiResponse<LoginData>>("/auth/login", { username, password })
    .send();

  if (!isResponseSuccess<LoginData>(res) || !res.data) {
    throw new Error(res.message || "Login gagal");
  }

  const loginData = res.data;
  setToken(loginData.tokens.access_token);
  setRefreshToken(loginData.tokens.refresh_token);

  return { success: true, message: res.message };
}

/* ==================== Provider ==================== */

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  let initialUser: User | null = null;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const decrypted = decryptData(stored);
      initialUser = decrypted ? (JSON.parse(decrypted) as User) : null;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  const [user, setUser] = useState<User | null>(initialUser);
  const [loading, setLoading] = useState<boolean>(true);

  /* =========== ACTIONS =========== */
  const actions: AuthActions = {
    setUser: (u) => {
      setUser(u);
      if (u) {
        const encrypted = encryptData(JSON.stringify(u));
        localStorage.setItem(STORAGE_KEY, encrypted);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    },

    clearUser: () => {
      setUser(null);
      localStorage.removeItem(STORAGE_KEY);
      setLoading(false);
    },

    logout: () => {
      clearAllTokens();
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
      setLoading(false);
    },

    setLoading,
  };

  const loadProfile = useCallback(async () => {
    if (!getToken()) {
      actions.clearUser();
      return;
    }

    try {
      const profile = await getProfile(true);

      if (JSON.stringify(profile) !== JSON.stringify(user)) {
        actions.setUser(profile);
      }
    } catch {
      actions.clearUser();
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      await loadProfile();
      setLoading(false);
    })();
  }, []);

  return (
    <AuthContext.Provider value={[{ user, loading }, actions]}>
      {children}
    </AuthContext.Provider>
  );
}

/* ==================== HOOK ==================== */

export function useAuth(): [AuthState, AuthActions] {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}